import { useQuery } from '@tanstack/react-query';

import type { Resource } from '@setvi/shared/interfaces';
import type { OpenResourceProps } from '@setvi/shared/providers/resource-opener';

import { usePrintOrderEnabled } from 'componentsV2/print-order';
import { getResourceV2Query } from 'Services';

/**
 * What the preview dialog needs to render: the document url, the resource the
 * cart action works on, and whether that action belongs on this screen.
 *
 * The resource is always fetched by id, so the dialog does not depend on how
 * complete the caller's object is — the cart action routes on HasLocation,
 * HasQRCode and HasCoverPage, and a search hit carries none of them.
 */
export const useResourcePreview = ({
  id,
  resource,
  hidePrintOrderAction
}: OpenResourceProps) => {
  const printOrderEnabled = usePrintOrderEnabled();

  // The endpoint resolves by the numeric `ID`, not the `ResourceID` string the
  // viewer uses.
  const numericId = resource?.ID ?? id;

  // `isInitialLoading` (not `isLoading`): in React Query v4 a disabled query
  // with no data still reports `isLoading`, which would show the loader forever.
  const { data, isInitialLoading } = useQuery<Resource>({
    ...getResourceV2Query(numericId ?? 0),
    enabled: numericId != null
  });

  // The fetch is authoritative for the print-order fields but does not return
  // everything a caller may hold, so the two are merged rather than swapped.
  const cartResource = data && ({ ...resource, ...data } as Resource);

  return {
    // The caller's url first so the document renders without waiting on the
    // fetch. `||`, not `??`, so an empty string does not win over a real url.
    url:
      resource?.DownloadURLWithSas ||
      data?.DownloadURLWithSas ||
      data?.DownloadURL,
    cartResource,
    showCartAction:
      printOrderEnabled && !!cartResource && !hidePrintOrderAction,
    loading: isInitialLoading
  };
};
